import { useHoneyGridApiFetch } from '@/queries/useHoneyGridApiFetch'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { phoneNumbersQueryKey } from './usePhoneNumbers'

import type { PhoneNumber } from 'honeygrid-types'

interface PurchasePhoneNumberParams {
	workspaceId: string
	phoneNumber?: string
	areaCode?: string
	friendlyName?: string
}

/**
 * Hook to purchase and provision a new phone number
 */
export const usePurchasePhoneNumber = () => {
	const queryClient = useQueryClient()
	const authFetch = useHoneyGridApiFetch()

	return useMutation({
		mutationFn: async ({
			workspaceId,
			phoneNumber,
			areaCode,
			friendlyName,
		}: PurchasePhoneNumberParams) => {
			if (!phoneNumber && !areaCode) {
				throw new Error('Must provide either phoneNumber or areaCode')
			}

			return authFetch(`workspaces/${workspaceId}/phone-numbers`, {
				method: 'POST',
				body: {
					phoneNumber,
					areaCode,
					friendlyName,
				},
			}) as Promise<PhoneNumber>
		},
		onSuccess: (data, { workspaceId }) => {
			queryClient.invalidateQueries({
				queryKey: phoneNumbersQueryKey(workspaceId),
			})
			toast.success(`Phone number ${data.phone_number} purchased successfully`)
		},
		onError: (error: Error) => {
			toast.error(`Failed to purchase phone number: ${error.message}`)
		},
	})
}
